/**
 * READ-LAYER INHERITANCE of a company's shared contact methods.
 *
 * A genuinely shared method — a front desk, a switchboard, `info@` — belongs to
 * the COMPANY, never to a person there (see CONTACT_METHOD_EXCLUSIVITY). The
 * 2026-08-21 remediation acted on that: it moved shared numbers off people and
 * onto their companies, which left roughly 745 contact rows in production
 * showing no phone number at all — the number was still reachable, just owned
 * one level up with nothing in the UI reading it.
 *
 * This module is that reader. It PROJECTS the company's shared methods onto its
 * people so a rep can still call the office from the person's card, while
 * keeping the two kinds of method structurally distinct: an inherited method
 * carries `scope: "company"` and is badged "Company line", so nobody mistakes a
 * switchboard for someone's direct line.
 *
 * It is a projection and nothing else:
 *
 *   - NOTHING here writes. Copying an inherited value back onto the contact row
 *     would re-create the exact collision the single-owner constraint forbids
 *     (`contact_method_claims` + `trg_contact_method_claims_single_owner`), and
 *     re-manufacture the duplicate "people" the model exists to prevent.
 *   - An inherited method is never editable. Surfaces must not give it a
 *     `fieldId` or an inline editor — the shared method is edited on the
 *     COMPANY page (`organization_phone` / `organization_email` and the alias
 *     array editors).
 *   - An anchor inherits nothing. `contacts.is_company_line = true` rows are not
 *     people; they hold no methods of their own and exist only because
 *     `communications.contact_id` is NOT NULL.
 *
 * Pure and client-safe on purpose — the same resolution has to run in the
 * detail header, the Details tab, and the detail modal's info panel, and all
 * three must agree on what is inherited. Keys come from
 * `@/lib/utils/contact/method-keys`, the TypeScript mirror of the SQL key
 * functions, so "the person already has this number" means the same thing here
 * as it does in Postgres.
 */

import { emailMethodKey, phoneMethodKey } from "@/lib/utils/contact/method-keys";
import type { Companies, Contacts } from "@/src/gql/graphql";

/** The one badge string every inherited-method surface renders. */
export const COMPANY_LINE_BADGE_LABEL = "Company line";

/** Which company column an inherited method came from. */
export type InheritedMethodSource =
  | "organization_phone"
  | "phone_aliases"
  | "organization_email"
  | "email_aliases";

/**
 * A contact method the person does NOT own.
 *
 * `scope: "company"` is a required literal rather than an optional flag so an
 * inherited method can never be passed where a personal one is expected without
 * the type system noticing — the API-shape half of "visibly and structurally
 * distinguishable".
 */
export interface InheritedContactMethod {
  readonly scope: "company";
  readonly kind: "phone" | "email";
  /** The shared value, trimmed. Display and dial/compose use this as-is. */
  readonly value: string;
  readonly companyId: string | null;
  readonly companyName: string | null;
  /**
   * The company's line-contact anchor, when it has one. Activity on a shared
   * line belongs to the anchor, not to the colleague whose card it was launched
   * from — see `inheritedMethodEntityContext`.
   */
  readonly lineContactId: string | null;
  readonly source: InheritedMethodSource;
}

/**
 * The person's own methods, and the company's shared ones.
 *
 * Both are `Pick<>`s of the CODEGEN entity types from `@/src/gql/graphql`, not
 * hand-rolled interfaces — the read/display half of the rule stated in
 * SLACK_FORMATTERS_USE_CODEGEN_TYPES. This resolver is the single place every
 * inherited-method surface reads these columns, so it is also the single place a
 * schema drift can be caught: rename or drop `organization_phone` and `Pick`
 * fails to compile HERE, at the definition, instead of silently resolving
 * nothing at runtime because a structural interface still described the old
 * shape. Run `npm run codegen` after any `.graphql` change so these stay current.
 *
 * `Partial<>` wraps the pick deliberately. Surfaces load different subsets of a
 * contact or company (a list row carries less than a detail header), and the
 * resolver already treats every field as optional. Partial keeps those callers
 * legal while `Pick` still binds every field NAME to the generated schema —
 * leniency about which fields arrive, zero leniency about what they are called.
 */
export type ContactOwnMethods = Partial<
  Pick<Contacts, "phone" | "phone_aliases" | "email" | "email_aliases" | "is_company_line">
>;

export type CompanySharedMethods = Partial<
  Pick<
    Companies,
    | "id"
    | "name"
    | "organization_phone"
    | "phone_aliases"
    | "organization_email"
    | "email_aliases"
    | "line_contact_id"
  >
>;

export interface InheritedContactMethods {
  /** Ordered: `organization_phone` first, then `phone_aliases` as stored. */
  readonly phones: readonly InheritedContactMethod[];
  readonly emails: readonly InheritedContactMethod[];
}

const NONE: InheritedContactMethods = { phones: [], emails: [] };

function cleanValues(
  primary: string | null | undefined,
  aliases: readonly (string | null)[] | null | undefined
): { value: string; fromPrimary: boolean }[] {
  const out: { value: string; fromPrimary: boolean }[] = [];
  const primaryTrimmed = primary?.trim();
  if (primaryTrimmed) out.push({ value: primaryTrimmed, fromPrimary: true });
  for (const alias of aliases ?? []) {
    const trimmed = alias?.trim();
    if (trimmed) out.push({ value: trimmed, fromPrimary: false });
  }
  return out;
}

function resolveKind(
  kind: "phone" | "email",
  own: { value: string }[],
  shared: { value: string; fromPrimary: boolean }[],
  company: CompanySharedMethods,
  key: (raw: string) => string | null
): InheritedContactMethod[] {
  // The person owns a method of this kind, so the company's shared one is not
  // how you reach THEM — their direct line wins and nothing is inherited.
  //
  // This also settles the legacy-conflict case for free: where a pre-remediation
  // duplicate still leaves the same number on both owners, the person's copy is
  // the one that renders, as theirs, exactly once. No dedupe pass is needed
  // because a person with any method of this kind inherits none.
  if (own.length > 0) return [];

  const seen = new Set<string>();
  const inherited: InheritedContactMethod[] = [];

  for (const { value, fromPrimary } of shared) {
    const k = key(value);
    // An unkeyable value (garbage, a too-short number) is still worth showing —
    // it is what a human would dial — but it cannot be deduped, so key on the
    // literal text instead.
    const dedupeKey = k ?? `raw:${value.toLowerCase()}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    inherited.push({
      scope: "company",
      kind,
      value,
      companyId: company.id ?? null,
      companyName: company.name ?? null,
      lineContactId: company.line_contact_id ?? null,
      source: fromPrimary
        ? kind === "phone"
          ? "organization_phone"
          : "organization_email"
        : kind === "phone"
          ? "phone_aliases"
          : "email_aliases",
    });
  }

  return inherited;
}

/**
 * Which of the company's shared methods this person inherits.
 *
 * Returns nothing at all when the contact is a company-line anchor, or when
 * there is no company. A kind is inherited only when the person owns no method
 * of that kind — their own direct line always wins, and is never shown as
 * inherited.
 */
export function resolveInheritedContactMethods(
  contact: ContactOwnMethods | null | undefined,
  company: CompanySharedMethods | null | undefined
): InheritedContactMethods {
  if (!contact || !company) return NONE;
  if (contact.is_company_line === true) return NONE;

  const ownPhones = cleanValues(contact.phone, contact.phone_aliases);
  const ownEmails = cleanValues(contact.email, contact.email_aliases);
  const sharedPhones = cleanValues(company.organization_phone, company.phone_aliases);
  const sharedEmails = cleanValues(company.organization_email, company.email_aliases);

  const phones = resolveKind("phone", ownPhones, sharedPhones, company, phoneMethodKey);
  const emails = resolveKind("email", ownEmails, sharedEmails, company, emailMethodKey);

  if (phones.length === 0 && emails.length === 0) return NONE;
  return { phones, emails };
}

/** Human-readable provenance for a badge tooltip / `title`. */
export function inheritedMethodTooltip(method: InheritedContactMethod): string {
  const owner = method.companyName?.trim() || "this company";
  const what = method.kind === "phone" ? "line" : "mailbox";
  return `Shared ${what} owned by ${owner} — not this person's direct ${
    method.kind === "phone" ? "number" : "address"
  }`;
}

/**
 * The entity context a compose / send-text modal should use for an INHERITED
 * method.
 *
 * A text to the front desk is the company's conversation, so it must be logged
 * against the company's line anchor — logging it on whichever colleague's card
 * the rep happened to be looking at is precisely the mis-attribution the anchor
 * exists to prevent. Returns `null` when the company has no anchor yet, and the
 * caller then keeps whatever context it already had: today's behaviour, no
 * worse, until the anchor is provisioned.
 */
export function inheritedMethodEntityContext(method: InheritedContactMethod): {
  item: Record<string, unknown>;
  entityType: string;
} | null {
  if (!method.lineContactId) return null;
  return {
    entityType: "contacts",
    item: {
      id: method.lineContactId,
      name: method.companyName
        ? `${method.companyName} — ${COMPANY_LINE_BADGE_LABEL}`
        : COMPANY_LINE_BADGE_LABEL,
      company_id: method.companyId ?? undefined,
      company_name: method.companyName ?? undefined,
    },
  };
}
